if (!customElements.get('bundle-price-handler')) {
  customElements.define('bundle-price-handler', class BundlePriceHandler extends HTMLElement {
    constructor() {
      super();
      this.priceElement = null;
      this.bundleInputs = [];
      this.originalPrices = {};
      this.variantIdInput = null;
      this.originalStickyPrices = {};
      this.bundleSizeInput = null;
      this.originalCompareAtPrice = null;
    }

    connectedCallback() {
      // Small delay to ensure DOM is fully ready
      setTimeout(() => {
        this.initPriceHandler();
        this.attachEventListeners();
      }, 100);
    }

    initPriceHandler() {
      // Find all bundle radio inputs
      this.bundleInputs = Array.from(document.querySelectorAll('input[name="properties[Bundle]"]'));
      
      // Find the price element to update
      this.priceElement = document.querySelector('[role="status"]#price-' + this.getAttribute('section-id'));
      
      if (!this.priceElement) {
        console.warn('Bundle Price Handler: Price element not found for section:', this.getAttribute('section-id'));
        return;
      }

      // Store original prices
      this.storeOriginalPrices();
      this.storeOriginalCompareAtPrice();
      this.storeOriginalStickyPrices(); // Call new method

      // Find the variant ID input to update when bundle changes
      this.variantIdInput = document.querySelector('product-form form input[name="id"]');
      if (!this.variantIdInput) {
        console.warn('Bundle Price Handler: Variant ID input not found');
      }

      // Create or find hidden input for bundle size
      this.bundleSizeInput = document.querySelector('input[name="properties[Bundle Size]"]');
      if (!this.bundleSizeInput) {
        // Create hidden input if it doesn't exist
        this.bundleSizeInput = document.createElement('input');
        this.bundleSizeInput.type = 'hidden';
        this.bundleSizeInput.name = 'properties[Bundle Size]';
        this.bundleSizeInput.value = '';
        
        // Find the product form and append the hidden input
        const productForm = document.querySelector('product-form form');
        if (productForm) {
          productForm.appendChild(this.bundleSizeInput);
        }
      }
      
      console.log('Bundle Price Handler initialized', {
        bundleInputsFound: this.bundleInputs.length,
        priceElementFound: !!this.priceElement,
        variantIdInputFound: !!this.variantIdInput
      });
      
      // Set initial product ID and bundle size
      this.updateBundleProductId();
      this.updateBundleSize();
      // Display the first bundle's price on initial load
      this.updatePrice();
    }

    storeOriginalPrices() {
      // Store all current price item values
      this.priceElement.querySelectorAll('.price-item').forEach((item, index) => {
        this.originalPrices[index] = item.textContent.trim();
      });
    }

    storeOriginalCompareAtPrice() {
      // Store the original compare-at price (strikethrough)
      const compareAtElement = this.priceElement.querySelector('s.price-item--regular');
      if (compareAtElement) {
        this.originalCompareAtPrice = compareAtElement.textContent.trim();
        console.log('Stored original compare-at price:', this.originalCompareAtPrice);
      }
    }

    storeOriginalStickyPrices() {
      // Find the sticky panel price container
      const stickyPriceContainer = document.querySelector('[data-sticky-panel-price]');
      if (!stickyPriceContainer) return;

      // Store all current sticky price item values
      stickyPriceContainer.querySelectorAll('.sticky-price-item').forEach((item, index) => {
        this.originalStickyPrices[index] = item.textContent.trim();
      });
    }

    attachEventListeners() {
      this.bundleInputs.forEach(input => {
        input.addEventListener('change', (e) => {
          console.log('Bundle selection changed:', e.target.getAttribute('data-bundle-product-title'));
          this.updatePrice();
          this.updateBundleProductId();
        });
      });
    }

    updateBundleProductId() {
      // Get the selected bundle input
      const selectedBundle = Array.from(this.bundleInputs).find(input => input.checked);
      
      if (!selectedBundle || !this.variantIdInput) {
        console.warn('Cannot update bundle product ID - missing selectedBundle or variantIdInput');
        return;
      }

      // Get bundle variant ID from data attribute (this is the correct ID to add to cart)
      const bundleVariantId = selectedBundle.getAttribute('data-bundle-variant-id');
      const bundleProductId = selectedBundle.getAttribute('data-bundle-product-id');
      
      if (bundleVariantId) {
        console.log('Updating product for cart submission:', {
          bundleProduct: selectedBundle.getAttribute('data-bundle-product-title'),
          bundleProductId: bundleProductId,
          bundleVariantId: bundleVariantId,
          previousVariantId: this.variantIdInput.value
        });

        // Set the variant ID input to the bundle product's variant ID
        // This ensures the bundle product variant gets added to cart instead of parent
        this.variantIdInput.value = bundleVariantId;
        
        console.log('✅ Cart updated - bundle product variant will be added:', {
          variantId: bundleVariantId,
          bundleName: selectedBundle.getAttribute('data-bundle-product-title')
        });
      } else {
        console.error('❌ Bundle variant ID not found in data attribute');
      }
    }

    updateBundleSize() {
      if (!this.bundleSizeInput || !this.bundleInputs.length) return;

      const selectedBundle = Array.from(this.bundleInputs).find(input => input.checked);
      if (!selectedBundle) return;

      const bundleSize = selectedBundle.getAttribute('data-bundle-size');
      if (bundleSize) {
        this.bundleSizeInput.value = bundleSize;
        console.log('Updated bundle size input:', { bundleSize: bundleSize });
      } else {
        this.bundleSizeInput.value = ''; // Clear if no size attribute
      }
    }

    updatePrice() {
      if (!this.priceElement) return;

      // Get the selected bundle input
      const selectedBundle = Array.from(this.bundleInputs).find(input => input.checked);
      
      if (!selectedBundle) return;

      // Get bundle price from data attribute (in cents)
      const bundlePriceInCents = parseFloat(selectedBundle.getAttribute('data-bundle-price')) || 0;
      const bundlePrice = bundlePriceInCents / 100; // Convert cents to dollars
      
      // Get bundle compare-at price from data attribute (in cents)
      const bundleCompareAtPriceCents = parseFloat(selectedBundle.getAttribute('data-bundle-compare-at-price')) || 0;
      const bundleCompareAtPrice = bundleCompareAtPriceCents / 100; // Convert cents to dollars
      
      console.log('Updating display price with bundle price:', bundlePrice);

      // Get the compare-at price from the strikethrough element
      const compareAtElement = this.priceElement.querySelector('s.price-item--regular');
      let compareAtPrice = null;
      if (compareAtElement) {
        compareAtPrice = this.parsePriceFromText(compareAtElement.textContent);
        console.log('📊 Bundle Price Details:', {
          bundlePrice: this.formatPrice(bundlePrice),
          bundleCompareAtPrice: bundleCompareAtPrice ? this.formatPrice(bundleCompareAtPrice) : 'N/A',
          compareAtPrice: compareAtPrice ? this.formatPrice(compareAtPrice) : 'N/A',
          bundleProductTitle: selectedBundle.getAttribute('data-bundle-product-title'),
          bundleSize: selectedBundle.getAttribute('data-bundle-size')
        });
      }

      // Update price items - be smart about which ones to update
      // We want to update the sale price (or regular price if no sale) but NOT the compare-at price
      const priceItems = this.priceElement.querySelectorAll('.price-item');
      
      priceItems.forEach((priceItem, index) => {
        // Check if this is a strikethrough element (compare-at price)
        // The <s> tag itself has the price-item--regular class, so check the tag name
        if (priceItem.tagName === 'S' || priceItem.parentElement.tagName === 'S') {
          // Update with bundle compare-at price if available, otherwise restore original
          if (bundleCompareAtPrice > 0) {
            priceItem.textContent = this.formatPrice(bundleCompareAtPrice);
            console.log('Updated compare-at price to bundle compare-at price:', this.formatPrice(bundleCompareAtPrice));
          } else if (this.originalCompareAtPrice) {
            priceItem.textContent = this.originalCompareAtPrice;
            console.log('Restored original compare-at price:', this.originalCompareAtPrice);
          }
          return; // Don't update strikethrough prices further
        }
        
        if (bundlePrice > 0) {
          // Replace with bundle price only
          priceItem.textContent = this.formatPrice(bundlePrice);
          console.log('Updated price item to bundle price:', { bundlePrice: this.formatPrice(bundlePrice) });
        } else {
          // Reset to original if no bundle price
          const originalText = this.originalPrices[index];
          if (originalText) {
            priceItem.textContent = originalText;
          }
        }
      });

      // Also update the sticky panel price if it exists
      this.updateStickyPanelPrice();
    }

    updateStickyPanelPrice() {
      // Find the sticky panel price container
      const stickyPriceContainer = document.querySelector('[data-sticky-panel-price]');
      if (!stickyPriceContainer) return;

      // Get the selected bundle input
      const selectedBundle = Array.from(this.bundleInputs).find(input => input.checked);
      if (!selectedBundle) return;

      // Get bundle price from data attribute (in cents)
      const bundlePriceInCents = parseFloat(selectedBundle.getAttribute('data-bundle-price')) || 0;
      const bundlePrice = bundlePriceInCents / 100; // Convert cents to dollars

      if (bundlePrice <= 0) {
        console.log('No bundle price to add to sticky panel');
        return;
      }

      console.log('Updating sticky panel price with bundle price:', bundlePrice);

      // Find all sticky price items and replace them with bundle price only, but skip compare-at prices
      const stickyPriceItems = stickyPriceContainer.querySelectorAll('.sticky-price-item');
      
      stickyPriceItems.forEach((priceItem, index) => {
        // Skip compare-at prices (strikethrough)
        if (priceItem.tagName === 'S' || priceItem.parentElement.tagName === 'S') {
          return; // Skip strikethrough prices
        }
        
        // Replace with bundle price only
        priceItem.textContent = this.formatPrice(bundlePrice);
        console.log('Updated sticky panel price to bundle price:', { bundlePrice: this.formatPrice(bundlePrice) });
      });
    }

    parsePriceFromText(priceText) {
      // Remove currency symbols, spaces and parse the number
      const cleaned = priceText.replace(/[^\d.,]/g, '');
      const match = cleaned.match(/[\d.,]+/);
      if (match) {
        const priceStr = match[0].replace(/,/g, '');
        return parseFloat(priceStr);
      }
      return null;
    }

    formatPrice(price) {
      // Format price with currency
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(price);
    }
  });
}
